/* inochi-features.js — Enhanced Squarespace-parity features for Inochi Tea House */
(function () {
  'use strict';

  // ── 1. Scroll-triggered Animations (Squarespace "Fade Up") ────────────
  function initScrollAnimations() {
    const sectionTargets = document.querySelectorAll(
      '.shopify-section:not(.inochi-hero-section):not(#shopify-section-header-group)'
    );

    sectionTargets.forEach((section) => {
      if (section.dataset.animInit) return;
      section.dataset.animInit = '1';
      section.classList.add('inochi-anim');

      // Stagger direct child blocks within the section
      const staggerCandidates = section.querySelectorAll(
        'h1, h2, h3, h4, p, .product-card, .collection-card__card, .blog-post-card, .button, img:not([loading="lazy"])'
      );
      staggerCandidates.forEach((el, i) => {
        el.style.setProperty('--child-delay', `${Math.min(i * 0.06, 0.48)}s`);
        el.classList.add('inochi-anim-child');
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('inochi-anim--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.07, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.inochi-anim').forEach((el) => observer.observe(el));
  }

  // ── 2. Hero Video Parallax ────────────────────────────────────────────
  function initHeroParallax() {
    const video = document.querySelector('.inochi-hero__video');
    if (!video) return;

    // Horizon uses .page-wrapper as the scroll container on desktop
    const scrollEl = document.querySelector('.page-wrapper') || window;
    let ticking = false;

    function applyParallax() {
      const scrollY = scrollEl.scrollTop !== undefined ? scrollEl.scrollTop : window.scrollY;
      // Subtle parallax: video moves at 40% scroll speed
      video.style.transform = `translateZ(0) translateY(${scrollY * 0.38}px)`;
      ticking = false;
    }

    scrollEl.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(applyParallax);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  // ── 3. Sticky Header — transparent → solid on scroll ──────────────────
  function initStickyHeader() {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection) return;

    const scrollEl = document.querySelector('.page-wrapper') || window;
    let lastScrollY = 0;

    function onScroll() {
      const scrollY = scrollEl.scrollTop !== undefined ? scrollEl.scrollTop : window.scrollY;
      const crossed = scrollY > 60;

      if (crossed !== lastScrollY > 60) {
        headerSection.classList.toggle('inochi-header--scrolled', crossed);
      }
      lastScrollY = scrollY;
    }

    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ── 4. Button Ripple Effect ───────────────────────────────────────────
  function initRipple() {
    document.addEventListener('pointerdown', (e) => {
      const btn = e.target.closest(
        '.inochi-hero__btn, .button:not(.button-unstyled), [class*="button-element"]'
      );
      if (!btn) return;

      const existing = btn.querySelector('.inochi-ripple');
      if (existing) existing.remove();

      const ripple = document.createElement('span');
      ripple.className = 'inochi-ripple';
      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2.2;

      ripple.style.cssText = `
        width:${size}px;
        height:${size}px;
        left:${e.clientX - rect.left - size / 2}px;
        top:${e.clientY - rect.top - size / 2}px;
      `;

      // Ensure btn can clip the ripple
      const pos = getComputedStyle(btn).position;
      if (pos === 'static') btn.style.position = 'relative';
      btn.style.overflow = 'hidden';

      btn.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
    });
  }

  // ── 5. Product & Collection Card Hover Zoom ───────────────────────────
  function initCardImageZoom() {
    // CSS handles the visual effect — we just ensure overflow is hidden on the image wrapper
    document.querySelectorAll(
      '.product-card__media, .collection-card__image, .featured-collection-card__media'
    ).forEach((wrapper) => {
      wrapper.style.overflow = 'hidden';
    });
  }

  // ── 6. Staggered Grid Entrance ────────────────────────────────────────
  function initGridStagger() {
    const grids = document.querySelectorAll(
      '.product-card-list, .collection-card-list, .blog-posts-list, .paginated-list__items'
    );

    grids.forEach((grid) => {
      Array.from(grid.children).forEach((item, i) => {
        item.style.setProperty('--grid-stagger', `${i * 0.08}s`);
        item.classList.add('inochi-grid-item');
      });

      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.querySelectorAll('.inochi-grid-item').forEach((item) => {
                item.classList.add('inochi-grid-item--visible');
              });
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05 }
      );
      obs.observe(grid);
    });
  }

  // ── 7. Smooth Section Dividers — adds wave class to eligible sections ──
  function initSectionDividers() {
    const sections = Array.from(
      document.querySelectorAll(
        '.shopify-section:not(.inochi-hero-section):not(#shopify-section-header-group):not(#shopify-section-footer-group)'
      )
    );

    sections.forEach((section, i) => {
      if (i === 0) return;
      // Mark alternating sections for subtle separator lines
      if (i % 2 === 0) section.classList.add('inochi-section--alt');
    });
  }

  // ── 8. Page-enter fade ────────────────────────────────────────────────
  function initPageFade() {
    document.body.classList.add('inochi-page-loaded');
  }

  // ── 9. Apostrophe / quote vertical alignment ──────────────────────────
  // The Inochi display font's apostrophes/quotes are sourced from a fallback
  // serif (see the unicode-range @font-face in theme.liquid). That fallback
  // renders them optically too high, so wrap each one and nudge it down via
  // the .inochi-apos class (styled in inochi.css).
  function fixQuoteHeight() {
    var QUOTE = /[‘’“”']/;
    var SPLIT = /([‘’“”'])/;
    var SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1, OPTION: 1, NOSCRIPT: 1, CODE: 1, PRE: 1 };
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || !QUOTE.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
        var p = node.parentNode;
        if (!p || SKIP[p.nodeName]) return NodeFilter.FILTER_REJECT;
        if (p.classList && p.classList.contains('inochi-apos')) return NodeFilter.FILTER_REJECT;
        if (p.closest && p.closest('input, textarea, [contenteditable]')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [], n;
    while ((n = walker.nextNode())) nodes.push(n);
    nodes.forEach(function (node) {
      var parts = node.nodeValue.split(SPLIT);
      if (parts.length < 2) return;
      var frag = document.createDocumentFragment();
      parts.forEach(function (part) {
        if (part === '') return;
        if (part.length === 1 && QUOTE.test(part)) {
          var span = document.createElement('span');
          span.className = 'inochi-apos';
          span.textContent = part;
          frag.appendChild(span);
        } else {
          frag.appendChild(document.createTextNode(part));
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────
  function init() {
    initScrollAnimations();
    initHeroParallax();
    initStickyHeader();
    initRipple();
    initCardImageZoom();
    initGridStagger();
    initSectionDividers();
    initPageFade();
    fixQuoteHeight();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
